import React from 'react';
import { Field } from 'redux-form'
import BaseModel from './BaseModel';
import EmailLayout from './EmailLayout';
import { Utils } from '../services';
import { normalizeIsoDuration } from '../services/isoDuration';
import {
    ColorPickerField,
    SettingsImagesField,
    SalepointsField,
    ArticlesField,
    CategoriesField,
    PaymentMethodsForm,
    IntegrationsForm,
    DeliveryTypesForm,
    PostCategoriesField,
    TranslatableField,
    UserDataField,
    UnitNumberField,
    DonationModesField,
    SettingsMobileField,
    RegistrationCallbackModesField,
    LocalesField,
    ExtraCurrenciesField,
    StringBoolean,
    PhoneField,
    BookingPolicyField,
    ToggleField,
    EmailField,
    EmailOverrideToField,
    CustomerTypeField,
    EmailLayoutsField,
    PolicyRefundField,
    EditionsField,
} from '../components';
import DefaultLayoutsForm from '../containers/Settings/DefaultLayoutsForm/DefaultLayoutsForm';
import DefaultLayoutsTabContext from '../containers/Settings/DefaultLayoutsForm/DefaultLayoutsTabContext';
import VotesNotBeforeField from '../components/VotesNotBeforeField/VotesNotBeforeField';
import DateTimePicker from '../components/DateTimePicker/DateTimePicker';
import ProcinemaFields from '../components/IntegrationsForm/ProcinemaFields/ProcinemaFields';
import * as _ from 'es-toolkit';

const form = "settingForm"

const CUSTOMER_TRANSLATABLE_FIELDS = [
    'name', 'name_short', 'phone', 'address',
    'favicon', 'appicon', 'mail',
];

/** Coerce broken shapes ([] / string) so TranslatableField can bind lang keys. */
function coerce_customer_translatable_value(value) {
    if (value == null || value === '')
        return {};
    if (Array.isArray(value))
        return {};
    if (typeof value === 'string')
        return { [Utils.default_lang()]: value };
    if (_.isPlainObject(value))
        return value;
    return {};
}

function normalize_setting_customer_translatable_fields(customer) {
    if (!customer || typeof customer !== 'object')
        return;

    CUSTOMER_TRANSLATABLE_FIELDS.forEach(field => {
        if (!(field in customer))
            return;
        customer[field] = coerce_customer_translatable_value(customer[field]);
    });

    if (customer.tax && typeof customer.tax === 'object' && ('number' in customer.tax))
        customer.tax.number = coerce_customer_translatable_value(customer.tax.number);

    if (customer.email && typeof customer.email === 'object' && ('mail' in customer.email))
        customer.email.mail = coerce_customer_translatable_value(customer.email.mail);
}

function clean_setting_customer_translatable_fields(customer) {
    if (!customer || typeof customer !== 'object')
        return;

    normalize_setting_customer_translatable_fields(customer);

    CUSTOMER_TRANSLATABLE_FIELDS.forEach(field => {
        Utils.cleanTranslatableProperty(customer, field);
    });

    if (customer.tax && typeof customer.tax === 'object')
        Utils.cleanTranslatableProperty(customer.tax, 'number');

    if (customer.email && typeof customer.email === 'object')
        Utils.cleanTranslatableProperty(customer.email, 'mail');
}

/** Class representing the Ticketack settings. */
class Setting extends BaseModel {
    constructor(properties) {
        super(properties);
        if (this.customer)
            normalize_setting_customer_translatable_fields(this.customer);
    }

    /**
     * Define the icon to use in the settings menu
     **/
    static propertyIcon(property, _default) {
        switch (property) {
            case 'customer':
                return 'vcard';
            case 'images':
                return 'photo';
            case 'pos':
                return 'credit-card';
            case 'eshop':
                return 'globe';
            case 'retention':
                return 'shield';
            case 'mobile':
                return 'mobile';
            case 'payment':
                return 'credit-card';
            case 'delivery':
                return 'truck';
            case 'l10n':
                return 'flag';
            case 'integrations':
                return 'external-link';
            case 'default_layouts':
            case 'pdf_layouts':
            case 'email_layouts':
                return 'file-alt';
            case 'votes':
                return 'star';
            default:
                return _default;
        };
    }

    /** Top-level keys present on a settings object (e.g. salepoint.settings), sorted like the settings browser. */
    static sortedTopLevelSettingKeys(t, setting) {
        if (!setting || typeof setting !== 'object') return [];
        const excluded = ['_id', 'created_at', 'updated_at', '_version'];
        return _.sortBy(
            Object.keys(setting).filter(k => !excluded.includes(k)),
            key => {
                const { order } = Setting.propertyForm(t, setting, key);
                return order;
            }
        );
    }

    /** Short label for a first-level settings section key (customer, eshop, …). */
    static topLevelSectionLabel(t, key) {
        const titleKey = `settings.${key}.title`;
        const title = t(titleKey);
        if (title !== titleKey) return title;
        const baseKey = `settings.${key}`;
        const base = t(baseKey);
        if (base !== baseKey) return base;
        return key;
    }

    /**
     * Define the Field input to use to change
     * each setting
     **/
    static propertyForm(t, setting, property, tickettypes, salepoints, articles, articleCategories, postCategories, places, layouts, overrides) {
        const noInput = (order, role) => { return { order, role, input: null }; };
        const strBoolInput = (order, property, selectText, role) => { return { order, role, input: <StringBoolean name={ property } defaultValue={Utils.getDotified(setting, property)} form={form} selectText={selectText} /> }; };
        const durationBoolInput = (order, property, selectText, role) => ({
            order,
            role,
            input: (
                <StringBoolean name={ property } defaultValue={Utils.getDotified(setting, property)} form={form} selectText={selectText}>
                    { form => (
                        <BookingPolicyField form={form} name={property} defaultValue={Utils.getDotified(setting, property)} />
                    )}
                </StringBoolean>
            )
        });
        const emailInput = (order, property, role) => { return { order, role, input: <EmailField name={ property } /> }; };
        const emailOverrideToInput = (order, property, role) => ({
            order,
            role,
            input: (
                <EmailOverrideToField
                    name={property}
                    defaultValue={Utils.getDotified(setting, property)}
                    form={form}
                />
            ),
        });
        const textInput = (order, property, role) => { return { order, role, input: <Field component="input" className="form-control" type="text" name={ property } /> }; };
        const translatableInput = (order, property, role) => { return { order, role, input: <TranslatableField component="input" name={ property } optional /> }; };
        const translatableTextarea = (order, property, role) => { return { order, role, input: <TranslatableField component="textarea" rows="3" name={ property } optional /> }; };
        const translatableEmailInput = (order, property, role) => { return { order, role, input: <EmailField translatable name={ property } /> }; };
        const requiredTranslatableEmailInput = (order, property, role) => {
            return { order, role, input: <EmailField required translatable name={ property } /> };
        };
        const translatablePhoneInput = (order, property, role) => { return { order, role, input: <TranslatableField component={PhoneField} name={ property } /> }; };
        const typeSelect = (order, property, role) => { return { order, role, input: <CustomerTypeField name={property} /> }}
        const numberInput = (order, property, role) => { return { order, role, input: <Field component="input" className="form-control" type="number" name={ property } /> }; };
        const unitNumberInput = (order, property, unit, min, max, valueFormat, role) => ({
            order,
            role,
            helpTooltip: property,
            input: (
                <Field
                    component={UnitNumberField}
                    name={property}
                    min={min}
                    max={max}
                    unit={unit}
                    valueFormat={valueFormat || 'number'}
                />
            ),
        });
        const extraCurrenciesInput = (order, property, role) => { return { order, role, input: <ExtraCurrenciesField name={ property } setting={ setting }/> }; };
        const urlInput = (order, property, role) => { return { order, role, input: <Field component="input" className="form-control" type="url" name={ property } pattern="https://.*" /> }; };
        const colorPickerInput = (order, property, role, options = {}) => {
            return { order, role, input: <ColorPickerField name={ property } { ...options } /> };
        };
        const clearableSurfaceColor = { optional: true, clearable: true, emptyValue: 'transparent' };
        const salepointInput = (order, property, role) => { return  { order, role, input: ( <SalepointsField name={ property } single salepoints={ salepoints } />) }; };
        const editionInput = (order, property, role) => { return  { order, role, input: ( <EditionsField name={ property } />) }; };
        const salepointsInput = (order, property, role) => { return  { order, role, input: ( <SalepointsField name={ property } salepoints={ salepoints } />) }; };
        const articlesInput = (order, property, role) => { return  { order, role, input: ( <ArticlesField name={ property } articles={ articles } single />) }; };
        const articleCategoryInput = (order, property, role) => { return  { order, role, input: ( <CategoriesField name={ property } categories={ articleCategories } />) }; };
        const postCategoryInput = (order, property, role) => { return  { order, role, input: ( <PostCategoriesField name={ property } postCategories={ postCategories } />) }; };
        const userDataFieldsInput = (order, property, role, options = {}) => {
            const { helpTooltip, ...userDataOptions } = options;
            return {
                order,
                role,
                ...(helpTooltip ? { helpTooltip } : {}),
                input: <UserDataField name={property} {...userDataOptions} />,
            };
        };
        const donationModesInput = (order, property, role) => { return  { order, role, input: ( <DonationModesField name={ property } />) }; };
        const registrationCallbackModesInput = (order, property, role) => { return  { order, role, input: ( <RegistrationCallbackModesField name={ property } />) }; };
        const paymentMethodsInput = (order, role) => { return  { order, role, hideLabel: true, input: ( <PaymentMethodsForm setting={setting} />) }; };
        const deliveryTypesInput = (order, role) => { return  { order, role, hideLabel: true, input: ( <DeliveryTypesForm />) }; };
        const localeInput = (order, property, role) => { return  { order, role, input: ( <LocalesField name={ property } single />) }; };
        const localesInput = (order, property, role) => { return  { order, role, input: ( <LocalesField name={ property } />) }; };
        const inlineImageInput = (order, property, role) => { return  { order, hideLabel: true, role, input: ( <SettingsImagesField name={ property } { ...overrides }/>) }; };
        const mobileInput = (order, property, role) => { return  { order, role, hideLabel: true, input: (<SettingsMobileField tickettypes={ tickettypes } salepoints={ salepoints } articles={ articles } articlecategories={ articleCategories } postCategories={ postCategories } places={ places } />) }; };
        const integrationInput = (order, property) => { return  { order, property, hideLabel: true, input: ( <IntegrationsForm />) }; };
        const procinemaInput = (order) => { return { order, hideLabel: true, input: (<ProcinemaFields />) }; };
        const booleanInput = (order, property, role) => {
            return  {
                order,
                role,
                input: (
                    <ToggleField name={ property } />
                )
            };
        };
        const emailLayoutInput = (order, property, role, types = null) => {
            return {
                order,
                role,
                input: (
                    <EmailLayoutsField
                        emailLayouts={layouts?.filter(l => l.layout_type === 'email')}
                        types={types}
                        name={property}
                        single
                    />
                )
            };
        }
        const policyRefundInput = (order, property, role) => ({
            order,
            role,
            input: <PolicyRefundField name={property} defaultValue={Utils.getDotified(setting, property)} />
        });

        switch (property) {
            // sections order
            case 'customer': return noInput(1);
            case 'images': return inlineImageInput(2, property);
            case 'pos': return noInput(3);
            case 'eshop': return noInput(4);
            case 'payment': return noInput(5);
            case 'delivery': return noInput(6);
            case 'mobile': return mobileInput(8, property);
            case 'l10n': return noInput(9);
            case 'votes': return noInput(10);
            // Salepoint overrides: recurse into integrations.* (IntegrationsForm is route-bound to global settings).
            case 'integrations': return overrides ? noInput(11) : integrationInput(11, property);
            case 'integrations.procinema': return procinemaInput(1);

            case 'customer.name': return translatableInput(1, property);
            case 'customer.name_short': return translatableInput(2, property);
            case 'customer.phone': return translatablePhoneInput(3, property);

            case 'customer.address': return translatableTextarea(4, property);
            case 'customer.type': return typeSelect(10, property);
            case 'customer.informal': return booleanInput(11, property);

            case 'customer.email': return noInput(10);
            case 'customer.email.mail': return requiredTranslatableEmailInput(1, property);
            case 'customer.email.bcc': return emailInput(1, property);
            case 'customer.email.errors_to': return emailInput(1, property);
            case 'customer.email.override_to': return emailOverrideToInput(1, property);
            // bwc
            case 'customer.mail': return translatableEmailInput(4, property);

            case 'email_layouts': return {
                order: 50,
                hideLabel: true,
                input: (
                    <DefaultLayoutsTabContext.Provider value="emails">
                        <DefaultLayoutsForm setting={setting} emailsOnly />
                    </DefaultLayoutsTabContext.Provider>
                ),
            };
            case 'email_layouts.order_confirmation':
                return emailLayoutInput(1, property, undefined, [EmailLayout.LAYOUT_TYPE_ORDER]);
            case 'email_layouts.distributor_report':
                return emailLayoutInput(2, property, undefined, [EmailLayout.LAYOUT_TYPE_DISTRIBUTOR_REPORT]);
            case 'email_layouts.booking_summary':
                return emailLayoutInput(3, property, undefined, [EmailLayout.LAYOUT_TYPE_BOOKING]);
            case 'email_layouts.refund_confirmation':
                return emailLayoutInput(4, property, undefined, [EmailLayout.LAYOUT_TYPE_REFUND]);
            case 'email_layouts.gift_notification':
                return emailLayoutInput(5, property, undefined, [EmailLayout.LAYOUT_TYPE_GIFT]);
            case 'pdf_layouts': return {
                order: 51,
                hideLabel: true,
                input: (
                    <DefaultLayoutsTabContext.Provider value="pdfs">
                        <DefaultLayoutsForm setting={setting} pdfOnly />
                    </DefaultLayoutsTabContext.Provider>
                ),
            };
            case 'default_layouts': return { order: 7, hideLabel: true, input: <DefaultLayoutsForm setting={setting} /> };

            case 'customer.favicon': return translatableInput(9, property);
            case 'customer.appicon': return translatableInput(10, property);
            case 'customer.tax': return noInput(11);
            case 'customer.tax.number': return translatableInput(11, property);
            case 'customer.tax.vat_articles': return numberInput(12, property);
            case 'customer.tax.vat_tickets': return numberInput(13, property);
            case 'customer.tax.suisa': return numberInput(14, property);
            case 'customer.tax.local': return numberInput(15, property);

            case 'l10n.currency': return textInput(1, property);
            case 'l10n.extra_currencies': return extraCurrenciesInput(2, property);
            case 'l10n.default_timezone': return textInput(3, property, 'superadmin');
            case 'l10n.locales': return noInput(4);
            case 'l10n.strftime_date_format': return textInput(5, property, 'superadmin');
            case 'l10n.strftime_datetime_format': return textInput(6, property, 'superadmin');
            case 'l10n.locales.all': return localesInput(7, property);
            case 'l10n.locales.default': return localeInput(8, property);
            case 'l10n.locales.display': return translatableInput(9, property);
            case 'l10n.charset': return textInput(10, property, 'superadmin');

            case 'pos.payment': return noInput(100);
            case 'pos.booking_cancelation_policy': return durationBoolInput(1, property, ["settings.pos.default_policy", "settings.pos.no_policy", "settings.pos.custom_policy"]);
            case 'pos.refund_policy': return policyRefundInput(-1, property);
            case 'pos.show_past_screenings_button': return booleanInput(1, property);
            case 'pos.cancel_items': return booleanInput(1, property);
            case 'pos.block_sales_while_booking': return booleanInput(1, property);
            case 'pos.ui_density': return {
                order: 2,
                input: (
                    <Field component="select" className="form-control" name={property}>
                        <option value="compact">{t('settings.pos.ui_density_compact')}</option>
                        <option value="default">{t('settings.pos.ui_density_default')}</option>
                        <option value="comfortable">{t('settings.pos.ui_density_comfortable')}</option>
                        <option value="spacious">{t('settings.pos.ui_density_spacious')}</option>
                    </Field>
                ),
            };
            case 'pos.show_print_addition_button': return booleanInput(1, property);
            case 'pos.show_send_order_button': return booleanInput(1, property);
            case 'pos.requested_buyer_data': return userDataFieldsInput(1, property);
            case 'pos.required_buyer_data': return userDataFieldsInput(1, property);
            case 'pos.screening_capacity_warning_level': return numberInput(80, property);
            case 'pos.hide_screenings_started_since': return unitNumberInput(81, property, 'minute', 0, 10080);
            case 'pos.additional_css': return urlInput(90, property);
            case 'pos.additional_js': return urlInput(91, property);

            case 'pos.automatic_printing': return noInput(200);
            case 'pos.automatic_printing.tickets': return booleanInput(1, property);
            case 'pos.automatic_printing.tickets_receipts': return booleanInput(2, property);
            case 'pos.automatic_printing.bookings': return booleanInput(3, property);
            case 'pos.automatic_printing.articles_receipts': return booleanInput(4, property);

            case 'eshop.emails': return noInput(50);
            case 'eshop.url_overrides': return noInput(100);
            case 'eshop.registration': return noInput(200);
            case 'eshop.payment': return noInput(300);
            case 'eshop.email': return noInput(400);
            case 'eshop.newsletter_registration': return noInput(500);
            case 'eshop.survey_registration': return noInput(600);
            case 'eshop.donation': return noInput(700);
            case 'retention': return noInput(800);
            case 'retention.privacy': return noInput(4);
            case 'retention.privacy.rules': return noInput(1);
            case 'retention.posthog': return noInput(5);

            case 'eshop.uri': return {
                order: 1,
                input: (
                    <Field
                        component="input"
                        className="form-control"
                        type="text"
                        name={property}
                        normalize={Utils.normalizeEshopUriInput}
                    />
                ),
            };
            case 'eshop.booking_cancelation_policy': return durationBoolInput(1, property, ["settings.pos.default_policy", "settings.pos.no_policy", "settings.pos.custom_policy"]);
            case 'eshop.booking_expiration_duration': return unitNumberInput(2, property, 'minute', 1, 10080, 'iso8601');
            case 'eshop.is_publicly_available': return booleanInput(3, property);
            case 'eshop.allow_search_engines': return {
                order: 4,
                helpTooltip: 'eshop.allow_search_engines',
                input: (
                    <Field component="select" className="form-control" name={property}>
                        <option value="all">{t('settings.eshop.allow_search_engines_all')}</option>
                        <option value="future_only">{t('settings.eshop.allow_search_engines_future_only')}</option>
                        <option value="none">{t('settings.eshop.allow_search_engines_none')}</option>
                    </Field>
                ),
            };
            case 'eshop.requested_buyer_data': return userDataFieldsInput(5, property);
            case 'eshop.required_buyer_data': return userDataFieldsInput(6, property);
            case 'eshop.max_tickets_in_cart': return numberInput(7, property);
            case 'eshop.max_tickets_per_screening': return numberInput(8, property);
            case 'eshop.iframe_integration': return booleanInput(9, property);
            case 'eshop.enable_promo_code': return booleanInput(10, property);
            case 'eshop.show_available_seats_to_customers': return booleanInput(11, property);

            case 'eshop.registration.enabled': return booleanInput(1, property);
            case 'eshop.registration.salepoints': return salepointsInput(2, property);
            case 'eshop.url_overrides.save_referer_for_host': return textInput(1, property);
            case 'eshop.url_overrides.home_url': return translatableInput(2, property);
            case 'eshop.url_overrides.screenings_url': return strBoolInput(3, property, ['settings.eshop.url_overrides.default_url', 'settings.eshop.url_overrides.no_url', 'settings.eshop.url_overrides.custom_url']);
            case 'eshop.url_overrides.screening_url': return strBoolInput(4, property, ['settings.eshop.url_overrides.default_url', 'settings.eshop.url_overrides.no_url', 'settings.eshop.url_overrides.custom_url']);
            case 'eshop.url_overrides.articles_url': return strBoolInput(5, property, ['settings.eshop.url_overrides.default_url', 'settings.eshop.url_overrides.no_url', 'settings.eshop.url_overrides.custom_url']);
            case 'eshop.url_overrides.article_url': return strBoolInput(6, property, ['settings.eshop.url_overrides.default_url', 'settings.eshop.url_overrides.no_url', 'settings.eshop.url_overrides.custom_url']);
            case 'eshop.url_overrides.pass_url': return strBoolInput(7, property, ['settings.eshop.url_overrides.default_url', 'settings.eshop.url_overrides.no_url', 'settings.eshop.url_overrides.custom_url']);
            case 'eshop.url_overrides.ticket_url': return strBoolInput(8, property, ['settings.eshop.url_overrides.default_url', 'settings.eshop.url_overrides.no_url', 'settings.eshop.url_overrides.custom_url']);
            case 'eshop.url_overrides.terms_conditions_url': return translatableInput(9, property);
            case 'eshop.url_overrides.privacy_policy_url': return translatableInput(10, property);
            case 'eshop.url_overrides.data_protection_url': return translatableInput(11, property);
            case 'eshop.url_overrides.sanitary_measures_url': return translatableInput(12, property);
            case 'eshop.url_overrides.anonymization_request_url': return translatableInput(13, property);
            case 'eshop.newsletter_registration.modes': return registrationCallbackModesInput(1, property);
            case 'eshop.newsletter_registration.auto_subscribe': return booleanInput(1, property);
            case 'eshop.survey_registration.modes': return registrationCallbackModesInput(1, property);
            case 'eshop.survey_registration.auto_subscribe': return booleanInput(1, property);
            case 'eshop.donation.modes': return donationModesInput(1, property);
            case 'eshop.donation.article': return articlesInput(1, property);
            case 'eshop.additional_css': return urlInput(90, property);
            case 'eshop.additional_js': return urlInput(91, property);

            case 'retention.enabled': return {
                order: 1,
                helpTooltip: 'retention.enabled',
                input: <ToggleField name={property} />,
            };
            case 'retention.abandoned_carts_days': return unitNumberInput(2, property, 'day', 1, 3650);
            case 'retention.later_payment_carts_days': return unitNumberInput(3, property, 'day', 1, 3650);
            case 'retention.system_logs_delete_after_years': return unitNumberInput(4, property, 'year', 1, 30);
            case 'retention.privacy.retained_fields': return userDataFieldsInput(2, property, undefined, { includeLanguage: true, helpTooltip: 'retention.privacy.retained_fields' });
            case 'retention.privacy.rules.inactive_grace_years': return unitNumberInput(1, property, 'year', 1, 30);
            case 'retention.privacy.rules.anonymize_after_years': return unitNumberInput(2, property, 'year', 1, 30);
            case 'retention.privacy.rules.delete_after_years': return unitNumberInput(3, property, 'year', 1, 30);
            case 'retention.privacy.dpo': return noInput(4);
            case 'retention.privacy.dpo.name': return {
                order: 1,
                helpTooltip: 'retention.privacy.dpo.help',
                input: <Field component="input" className="form-control" type="text" name={property} />,
            };
            case 'retention.privacy.dpo.email': return {
                order: 2,
                helpTooltip: 'retention.privacy.dpo.help',
                input: <EmailField name={property} />,
            };
            case 'retention.privacy.dpo.phone': return {
                order: 3,
                helpTooltip: 'retention.privacy.dpo.help',
                input: <Field component={PhoneField} name={property} />,
            };
            case 'retention.posthog.enabled': return {
                order: 1,
                helpTooltip: 'retention.posthog.enabled',
                input: <ToggleField name={property} />,
            };
            case 'retention.posthog.retention_months': return {
                order: 2,
                helpTooltip: 'retention.posthog.retention_months',
                input: (
                    <Field
                        component={UnitNumberField}
                        name={property}
                        min={1}
                        max={120}
                        unit="month"
                        valueFormat="number"
                        disabled
                    />
                ),
            };

            case 'payment.methods': return paymentMethodsInput(1);
            case 'delivery.types': return deliveryTypesInput(1);
            case 'votes.enabled': return booleanInput(1, property);
            case 'votes.not_before': return {
                order: 2,
                helpTooltip: 'votes.not_before',
                input: <Field component={VotesNotBeforeField} name={property} />,
            };
            case 'votes.not_after': return {
                order: 3,
                helpTooltip: 'votes.not_after',
                input: (
                    <Field
                        component={DateTimePicker}
                        name={property}
                        minBoundField="votes.not_before"
                    />
                ),
            };
            case 'votes.max_score': return numberInput(4, property);
            case 'votes.step': return numberInput(5, property);
            case 'votes.needs_scan': return booleanInput(6, property);


            case 'mobile.production.edition':
            case 'mobile.preprod.edition':
            case 'mobile.testing.edition':
                return editionInput(0, property);
            case 'mobile.production.appName':
            case 'mobile.preprod.appName':
            case 'mobile.testing.appName':
                return {
                    order: 1,
                    input: (
                        <Field
                            component="input"
                            className="form-control"
                            type="text"
                            name={ property }
                            maxLength={ 30 }
                            autoComplete="off"
                        />
                    ),
                };
            case 'mobile.production.kronosUri':
            case 'mobile.preprod.kronosUri':
            case 'mobile.testing.kronosUri':
                return textInput(2, property, 'superadmin');
            case 'mobile.production.kronosApiKey':
            case 'mobile.preprod.kronosApiKey':
            case 'mobile.testing.kronosApiKey':
                return textInput(3, property, 'superadmin');
            case 'mobile.production.salepointId':
            case 'mobile.preprod.salepointId':
            case 'mobile.testing.salepointId':
                return salepointInput(4, property);
            case 'mobile.production.disableUserAccount':
            case 'mobile.preprod.disableUserAccount':
            case 'mobile.testing.disableUserAccount':
                return booleanInput(4, property);
            case 'mobile.production.disableVotes':
            case 'mobile.preprod.disableVotes':
            case 'mobile.testing.disableVotes':
                return booleanInput(5, property);
            case 'mobile.production.disableFavorites':
            case 'mobile.preprod.disableFavorites':
            case 'mobile.testing.disableFavorites':
                return booleanInput(6, property);
            case 'mobile.production.enablePushNotifications':
            case 'mobile.preprod.enablePushNotifications':
            case 'mobile.testing.enablePushNotifications':
                return booleanInput(7, property);
            case 'mobile.production.hideVideos':
            case 'mobile.preprod.hideVideos':
            case 'mobile.testing.hideVideos':
                return booleanInput(8, property);
            case 'mobile.production.apiKey':
            case 'mobile.preprod.apiKey':
            case 'mobile.testing.apiKey':
            case 'mobile.production.engineUri':
            case 'mobile.preprod.engineUri':
            case 'mobile.testing.engineUri':
            case 'mobile.production.screeningsFilters':
            case 'mobile.preprod.screeningsFilters':
            case 'mobile.testing.screeningsFilters':
            case 'mobile.production.homeDisplay':
            case 'mobile.preprod.homeDisplay':
            case 'mobile.testing.homeDisplay':
                return noInput(100);
            case 'mobile.production.shop':
            case 'mobile.preprod.shop':
            case 'mobile.testing.shop':
                return noInput(300);
            case 'mobile.production.shop.rootCategoryId':
            case 'mobile.preprod.shop.rootCategoryId':
            case 'mobile.testing.shop.rootCategoryId':
                return articleCategoryInput(300, property);
            case 'mobile.production.posts':
            case 'mobile.preprod.posts':
            case 'mobile.testing.posts':
                return noInput(400);
            case 'mobile.production.posts.discover' :
            case 'mobile.preprod.posts.discover' :
            case 'mobile.testing.posts.discover' :
                return postCategoryInput(401, property);
            case 'mobile.production.posts.goodDeals' :
            case 'mobile.preprod.posts.goodDeals' :
            case 'mobile.testing.posts.goodDeals' :
                return postCategoryInput(402, property);
            case 'mobile.production.posts.pubs' :
            case 'mobile.preprod.posts.pubs' :
            case 'mobile.testing.posts.pubs' :
                return noInput(403);
            case 'mobile.production.posts.pubs.home' :
            case 'mobile.preprod.posts.pubs.home' :
            case 'mobile.testing.posts.pubs.home' :
                return postCategoryInput(404, property);
            case 'mobile.production.posts.pubs.shop' :
            case 'mobile.preprod.posts.pubs.shop' :
            case 'mobile.testing.posts.pubs.shop' :
                return postCategoryInput(405, property);
            case 'mobile.production.posts.pubs.program' :
            case 'mobile.preprod.posts.pubs.program' :
            case 'mobile.testing.posts.pubs.program' :
                return postCategoryInput(406, property);
            case 'mobile.production.posts.news' :
            case 'mobile.preprod.posts.news' :
            case 'mobile.testing.posts.news' :
                return noInput(407);
            case 'mobile.production.posts.news.rootCategoryId' :
            case 'mobile.preprod.posts.news.rootCategoryId' :
            case 'mobile.testing.posts.news.rootCategoryId' :
                return postCategoryInput(408, property);
            case 'mobile.production.featured':
            case 'mobile.preprod.featured':
            case 'mobile.testing.featured':
                return noInput(500);
            case 'mobile.production.featured.screening':
            case 'mobile.preprod.featured.screening':
            case 'mobile.testing.featured.screening':
                return textInput(501, property);
            case 'mobile.production.featured.movie':
            case 'mobile.preprod.featured.movie':
            case 'mobile.testing.featured.movie':
                return textInput(502, property);
            case 'mobile.production.images':
            case 'mobile.preprod.images':
            case 'mobile.testing.images':
                return noInput(600);
            case 'mobile.production.images.format':
            case 'mobile.preprod.images.format':
            case 'mobile.testing.images.format':
                return {
                    order: 601,
                    helpTooltip: 'mobile.images.format',
                    input: (
                        <Field
                            component="input"
                            className="form-control"
                            type="number"
                            step="any"
                            min={ 0.1 }
                            max={ 4 }
                            name={ property }
                        />
                    ),
                };
            case 'mobile.production.images.nb_per_row':
            case 'mobile.preprod.images.nb_per_row':
            case 'mobile.testing.images.nb_per_row':
                return {
                    order: 602,
                    helpTooltip: 'mobile.images.nb_per_row',
                    input: (
                        <Field
                            component="input"
                            className="form-control"
                            type="number"
                            step="any"
                            min={ 0.5 }
                            max={ 10 }
                            name={ property }
                        />
                    ),
                };
            case 'mobile.production.theme':
            case 'mobile.preprod.theme':
            case 'mobile.testing.theme':
                return noInput(700);
            case 'mobile.production.theme.colors':
            case 'mobile.preprod.theme.colors':
            case 'mobile.testing.theme.colors':
                return noInput(700);
            case 'mobile.production.theme.colors.background' :
            case 'mobile.preprod.theme.colors.background' :
            case 'mobile.testing.theme.colors.background' :
                return colorPickerInput(700, property, undefined, clearableSurfaceColor);
            case 'mobile.production.theme.colors.primary' :
            case 'mobile.preprod.theme.colors.primary' :
            case 'mobile.testing.theme.colors.primary' :
                return colorPickerInput(701, property);
            case 'mobile.production.theme.colors.primary-inverse' :
            case 'mobile.preprod.theme.colors.primary-inverse' :
            case 'mobile.testing.theme.colors.primary-inverse' :
                return colorPickerInput(701, property);
            case 'mobile.production.theme.colors.nav':
            case 'mobile.preprod.theme.colors.nav':
            case 'mobile.testing.theme.colors.nav':
                return noInput(700);
            case 'mobile.production.theme.colors.nav.background' :
            case 'mobile.preprod.theme.colors.nav.background' :
            case 'mobile.testing.theme.colors.nav.background' :
                return colorPickerInput(702, property, undefined, clearableSurfaceColor);
            case 'mobile.production.theme.colors.nav.border' :
            case 'mobile.preprod.theme.colors.nav.border' :
            case 'mobile.testing.theme.colors.nav.border' :
                // Empty or "transparent" means no tab-bar line. Ink colors stay required.
                return {
                    order: 703,
                    input: <ColorPickerField name={ property } optional clearable />,
                };
            case 'mobile.production.theme.colors.nav.activeColor' :
            case 'mobile.preprod.theme.colors.nav.activeColor' :
            case 'mobile.testing.theme.colors.nav.activeColor' :
                return colorPickerInput(704, property);
            case 'mobile.production.theme.colors.nav.inactiveColor' :
            case 'mobile.preprod.theme.colors.nav.inactiveColor' :
            case 'mobile.testing.theme.colors.nav.inactiveColor' :
                return colorPickerInput(705, property);
            case 'mobile.production.theme.statusBarBackgroundColor':
            case 'mobile.preprod.theme.statusBarBackgroundColor':
            case 'mobile.testing.theme.statusBarBackgroundColor':
                return {
                    order: 698,
                    input: <ColorPickerField name={ property } optional clearable />,
                };

            default: return textInput(1000, property);
        }
    }

    /**
     * Prepare this object for update.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForUpdate() {
        const prepared = super.prepareForUpdate();

        clean_setting_customer_translatable_fields(prepared.customer);

        // Only normalize delivery when present. Inventing `{ types: [] }` on
        // partial redux-form submits (e.g. default layouts) would wipe types
        // and omit `_id`, producing PUT .../id/undefined.
        if (prepared.delivery != null) {
            prepared.delivery.types = (prepared.delivery.types || []).map(type => {
                Utils.cleanTranslatableProperty(type, "name");
                type.rules = (type.rules || []).map(rule => {
                    _.mapKeys(rule, (value, property) => {
                        if (['price', 'min_cart_amount'].includes(property)) {
                            _.mapKeys(rule[property], (price, currency) => {
                                rule[property][currency] = Number(price);
                                if (_.isNaN(rule[property][currency]))
                                    rule[property][currency] = 0;
                            });
                        } else if (property !== 'countries') {
                            rule[property] = Number(rule[property]);
                            if (_.isNaN(rule[property]))
                                delete rule[property];
                        }
                    });

                    return rule;
                });

                return type;
            });
        }

        if (prepared.eshop?.booking_expiration_duration != null) {
            prepared.eshop.booking_expiration_duration = normalizeIsoDuration(
                prepared.eshop.booking_expiration_duration,
                'minute'
            );
        }

        if (typeof prepared.eshop?.uri === 'string')
            prepared.eshop.uri = Utils.normalizeEshopUri(prepared.eshop.uri);

        return prepared;
    }

    getScreeningsImportConfiguration() {
        let configuration = null
        Object.keys(this.integrations || {}).forEach(source => {
            if (configuration)
                return;

            const config = this.integrations[source];
            if (config?.import?.screenings?.enabled)
                configuration = {
                    ...config,
                    source
                };
        });

        return configuration;
    }

    getTicketsImportConfiguration() {
        let configuration = null
        Object.keys(this.integrations || {}).forEach(source => {
            if (configuration)
                return;

            const config = this.integrations[source];
            if (config?.import?.tickets?.enabled)
                configuration = {
                    ...config,
                    source
                };
        });

        return configuration;
    }

    findImage(type, lang) {
        const images = this.images || [];
        if (images.length === 0)
            return null;

        // check the type (mandatory)
        let matchingType = images.filter(image => image.type === type);
        if (matchingType.length === 0) {
            // logo to logo A4 to logo thermal fallback
            if (type === Utils.IMAGE_TYPE_LOGO) {
                matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_LOGO_A4);
                if (matchingType.length === 0) {
                    matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_LOGO_THERMAL);
                }
                if (matchingType.length === 0) {
                    return null;
                }
            }
            // logo A4 to logo to logo thermal fallback
            else if (type === Utils.IMAGE_TYPE_LOGO_A4) {
                matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_LOGO);
                if (matchingType.length === 0) {
                    matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_LOGO_THERMAL);
                }
                if (matchingType.length === 0) {
                    return null;
                }
            }
            // logo thermal to logo A4 to logo fallback
            else if (type === Utils.IMAGE_TYPE_LOGO_THERMAL) {
                matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_LOGO_A4);
                if (matchingType.length === 0) {
                    matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_LOGO);
                }
                if (matchingType.length === 0) {
                    return null;
                }
            }
            // partners to partners A4 to partners thermal fallback
            else if (type === Utils.IMAGE_TYPE_PARTNERS) {
                matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_PARTNERS_A4);
                if (matchingType.length === 0) {
                    matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_PARTNERS_THERMAL);
                }
                if (matchingType.length === 0) {
                    return null;
                }
            }
            // partners A4 to partners to partners thermal fallback
            else if (type === Utils.IMAGE_TYPE_PARTNERS_A4) {
                matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_PARTNERS);
                if (matchingType.length === 0) {
                    matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_PARTNERS_THERMAL);
                }
                if (matchingType.length === 0) {
                    return null;
                }
            }
            // partners thermal to partners A4 to partners fallback
            else if (type === Utils.IMAGE_TYPE_PARTNERS_THERMAL) {
                matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_PARTNERS_A4);
                if (matchingType.length === 0) {
                    matchingType = images.filter(image => image.type === Utils.IMAGE_TYPE_PARTNERS);
                }
                if (matchingType.length === 0) {
                    return null;
                }
            }
            if (matchingType.length === 0) {
                return null;
           }
        }

        // check the lang
        const matchinglang = matchingType.filter(image => image.lang === lang);
        if (matchinglang.length > 0)
            return matchinglang[0];

        // fallback to images without lang
        const withoutLang = matchingType.filter(image => (image.lang === null || image.lang === ""));
        if (withoutLang.length > 0)
            return withoutLang[0];

        // fallback to unmatching lang
        return matchingType[0];
    };
}

export default Setting;
